
// Traffic lights as in the customer status reports of 2026-10-17, docs/roadmap/06-dashboard.md
export type Status = 'excellent' | 'good' | 'warn' | 'crit' | 'low' | 'nodata'

export const MIN_DUE = 5

export function completionStatus(pct: number | null | undefined, due: number): Status {
  if (pct === null || pct === undefined || due < MIN_DUE) return 'nodata'
  if (pct >= 90) return 'excellent'
  if (pct >= 80) return 'good'
  if (pct >= 50) return 'warn'
  if (pct >= 20) return 'crit'
  return 'low'
}

// status is never shown by colour alone: every chip also has an icon and a label
export const STATUS_STYLE: Record<Status, { bg: string, fg: string, bar: string, icon: string }> = {
  excellent: { bg: '#D3F0DE', fg: '#14532D', bar: '#1E7A46', icon: 'M2 12l4 4 8-8M10 16l8-8' },
  good: { bg: '#E3F4EA', fg: '#1D6B43', bar: '#2E8A57', icon: 'M5 12l4 4 10-10' },
  warn: { bg: '#FFF4E0', fg: '#8A4B00', bar: '#C77700', icon: 'M12 7v6M12 17h.01' },
  crit: { bg: '#FDEBE3', fg: '#A5370A', bar: '#D9520F', icon: 'M12 7v6M12 17h.01' },
  low: { bg: '#FDECEA', fg: '#A3201A', bar: '#B42318', icon: 'M7 7l10 10M17 7L7 17' },
  nodata: { bg: '#EEF1F5', fg: '#4B5565', bar: '#8F9AAA', icon: 'M6 12h12' }
}

export function heatColor(value: number | null): { bg: string, fg: string } {
  if (value === null) return { bg: '#F7F9FC', fg: '#8F9AAA' }
  if (value >= 80) return { bg: '#0B4E98', fg: '#FFFFFF' }
  if (value >= 60) return { bg: '#2F7BD0', fg: '#FFFFFF' }
  if (value >= 40) return { bg: '#6CA6E8', fg: '#142033' }
  if (value >= 15) return { bg: '#BFD7F3', fg: '#142033' }
  return { bg: '#EEF1F5', fg: '#142033' }
}

// polyline points for a sparkline; weeks without data are skipped
export function sparklinePoints(values: (number | null)[], width: number, height: number, max?: number): string {
  if (values.length < 2) return ''
  const top = max ?? Math.max(1, ...values.filter((v): v is number => v !== null))
  return values
    .map((v, i) => (v === null ? null
      : `${Math.round(2 + (i * (width - 4)) / (values.length - 1))},${Math.round(height - 2 - (v / top) * (height - 4))}`))
    .filter(Boolean)
    .join(' ')
}

export type Trend = 'better' | 'worse' | 'same' | 'none'

export function trend(value: number | null, previous: number | null, higherIsBetter = true): Trend {
  if (value === null || previous === null) return 'none'
  if (value === previous) return 'same'
  return (value > previous) === higherIsBetter ? 'better' : 'worse'
}

export const TREND_COLOR: Record<Trend, string> = {
  better: '#1D6B43', worse: '#A5370A', same: '#4B5565', none: '#4B5565'
}
